import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
    Card, Button, Input, Select, Form, Drawer, Tag, Space,
    Tooltip, Popconfirm, message, Spin, Badge, Row, Col,
    InputNumber, Switch, Collapse, Statistic
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, ArrowLeftOutlined,
    ApartmentOutlined, BarsOutlined, SaveOutlined, ImportOutlined,
    DownOutlined, RightOutlined, CheckCircleOutlined, DownloadOutlined
} from '@ant-design/icons';
import MeService from '../../services/MeService';

const { Option }   = Select;
const { TextArea } = Input;
const { Panel }    = Collapse;

// ── Constants ─────────────────────────────────────────────────────────────
const INDICATOR_TYPES = [
    { value:'requirement', label:'Requirement', color:'#1890ff', desc:'Must comply (ISO, standard)' },
    { value:'output',      label:'Output',      color:'#52c41a', desc:'Something produced (workplan)' },
    { value:'outcome',     label:'Outcome',     color:'#722ed1', desc:'Change achieved (strategy)' },
    { value:'process',     label:'Process',     color:'#fa8c16', desc:'Activity/process to be done' },
    { value:'milestone',   label:'Milestone',   color:'#13c2c2', desc:'Key date/deliverable' },
    { value:'kpi',         label:'KPI',         color:'#eb2f96', desc:'Key performance indicator' },
];

const MEASUREMENT_UNITS = [
    { value:'yes_no',     label:'Yes / No' },
    { value:'percentage', label:'Percentage (%)' },
    { value:'count',      label:'Count (number)' },
    { value:'score',      label:'Score' },
    { value:'date',       label:'Date (milestone)' },
    { value:'text',       label:'Narrative only' },
    { value:'currency',   label:'Currency (E)' },
];

const FREQUENCIES = [
    { value:'once',      label:'Once only' },
    { value:'monthly',   label:'Monthly' },
    { value:'quarterly', label:'Quarterly' },
    { value:'biannual',  label:'Bi-annual' },
    { value:'annual',    label:'Annual' },
];

const getIndicatorType = val => INDICATOR_TYPES.find(t => t.value === val) || INDICATOR_TYPES[0];

// ── Section Form Drawer ───────────────────────────────────────────────────
function SectionForm({ visible, onClose, onSaved, frameworkId, editRecord, sections }) {
    const [form]   = Form.useForm();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (visible) {
            if (editRecord) {
                form.setFieldsValue(editRecord);
            } else {
                form.resetFields();
                form.setFieldsValue({ framework_id: frameworkId, weight: 1, display_order: 0 });
            }
        }
    }, [visible, editRecord]); // eslint-disable-line

    const onFinish = async (values) => {
        setSaving(true);
        try {
            const payload = {
                ...values,
                id:           editRecord?.id || null,
                framework_id: frameworkId,
                parent_id:    values.parent_id || null,
            };
            const r = await MeService.saveSection(payload);
            if (r.data?.code !== 200) throw new Error(r.data?.message);
            message.success('Section saved');
            onSaved();
            onClose();
        } catch(e) { message.error(e.message || 'Save failed'); }
        finally { setSaving(false); }
    };

    // Flat sections for parent selector (exclude self)
    const parentOptions = sections.filter(s => s.id !== editRecord?.id);

    return (
        <Drawer
            title={<span style={{ fontWeight:800 }}>{editRecord ? '✏️ Edit Section' : '➕ New Section'}</span>}
            open={visible} onClose={onClose} width={480} destroyOnClose
            footer={
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" loading={saving} onClick={() => form.submit()}
                        style={{ background:'#1f3864', borderColor:'#1f3864' }}>
                        Save Section
                    </Button>
                </div>
            }
        >
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Row gutter={12}>
                    <Col span={8}>
                        <Form.Item name="code" label="Code" rules={[{ required:true }]}>
                            <Input placeholder="6, 6.1, SA2"/>
                        </Form.Item>
                    </Col>
                    <Col span={16}>
                        <Form.Item name="title" label="Title" rules={[{ required:true }]}>
                            <Input placeholder="Section title"/>
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="parent_id" label="Parent Section">
                    <Select allowClear placeholder="Top-level (no parent)">
                        {parentOptions.map(s => (
                            <Option key={s.id} value={s.id}>
                                {s.code} — {s.title}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item name="description" label="Description">
                    <TextArea rows={2} placeholder="What this section covers..."/>
                </Form.Item>
                <Row gutter={12}>
                    <Col span={12}>
                        <Form.Item name="weight" label="Weight">
                            <InputNumber min={0} max={100} step={0.1} style={{ width:'100%' }}/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="display_order" label="Display Order">
                            <InputNumber min={0} style={{ width:'100%' }}/>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Drawer>
    );
}

// ── Indicator Form Drawer ─────────────────────────────────────────────────
function IndicatorForm({ visible, onClose, onSaved, frameworkId, sectionId, editRecord }) {
    const [form]   = Form.useForm();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (visible) {
            if (editRecord) {
                form.setFieldsValue(editRecord);
            } else {
                form.resetFields();
                form.setFieldsValue({
                    framework_id:     frameworkId,
                    section_id:       sectionId,
                    indicator_type:   'requirement',
                    measurement_unit: 'yes_no',
                    frequency:        'quarterly',
                    is_mandatory:     true,
                    is_critical:      false,
                    weight:           1,
                    display_order:    0,
                    status:           1,
                });
            }
        }
    }, [visible, editRecord, sectionId]); // eslint-disable-line

    const onFinish = async (values) => {
        setSaving(true);
        try {
            const payload = {
                ...values,
                id:           editRecord?.id || null,
                framework_id: frameworkId,
                section_id:   sectionId || values.section_id,
                is_mandatory: values.is_mandatory ? 1 : 0,
                is_critical:  values.is_critical  ? 1 : 0,
                status:       values.status ? 1 : 0,
                created_by:   0,
            };
            const r = await MeService.saveIndicator(payload);
            if (r.data?.code !== 200) throw new Error(r.data?.message);
            message.success('Indicator saved');
            onSaved();
            onClose();
        } catch(e) { message.error(e.message || 'Save failed'); }
        finally { setSaving(false); }
    };

    return (
        <Drawer
            title={<span style={{ fontWeight:800 }}>{editRecord ? '✏️ Edit Indicator' : '➕ New Indicator'}</span>}
            open={visible} onClose={onClose} width={560} destroyOnClose
            footer={
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" loading={saving} onClick={() => form.submit()}
                        style={{ background:'#1f3864', borderColor:'#1f3864' }}>
                        Save Indicator
                    </Button>
                </div>
            }
        >
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Row gutter={12}>
                    <Col span={8}>
                        <Form.Item name="code" label="Code">
                            <Input placeholder="6.1.1, Act-01"/>
                        </Form.Item>
                    </Col>
                    <Col span={16}>
                        <Form.Item name="title" label="Title / Requirement" rules={[{ required:true }]}>
                            <Input placeholder="What is being measured or required"/>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="description" label="Detailed Description">
                    <TextArea rows={3} placeholder="Full description of the requirement or activity..."/>
                </Form.Item>

                <Row gutter={12}>
                    <Col span={12}>
                        <Form.Item name="indicator_type" label="Type">
                            <Select>
                                {INDICATOR_TYPES.map(t => (
                                    <Option key={t.value} value={t.value}>
                                        <Tag color={t.color} style={{ marginRight:4 }}>{t.label}</Tag>
                                        <span style={{ fontSize:11, color:'#888' }}>{t.desc}</span>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="measurement_unit" label="Measurement Unit">
                            <Select>
                                {MEASUREMENT_UNITS.map(u => (
                                    <Option key={u.value} value={u.value}>{u.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={12}>
                    <Col span={8}>
                        <Form.Item name="baseline_value" label="Baseline">
                            <Input placeholder="Current state"/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="target_value" label="Target">
                            <Input placeholder="e.g. 100%, 5000"/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="frequency" label="Reporting Frequency">
                            <Select>
                                {FREQUENCIES.map(f => (
                                    <Option key={f.value} value={f.value}>{f.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="data_source" label="Data Source">
                    <Input placeholder="Where does the evidence come from?"/>
                </Form.Item>

                <Form.Item name="responsible_role" label="Responsible Role">
                    <Input placeholder="Job title responsible for this indicator"/>
                </Form.Item>

                <Form.Item name="verification_method" label="Verification Method">
                    <Input placeholder="How will the assessor verify this?"/>
                </Form.Item>

                <Row gutter={12}>
                    <Col span={6}>
                        <Form.Item name="weight" label="Weight">
                            <InputNumber min={0} max={100} step={0.1} style={{ width:'100%' }}/>
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="display_order" label="Order">
                            <InputNumber min={0} style={{ width:'100%' }}/>
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="is_mandatory" label="Mandatory" valuePropName="checked">
                            <Switch checkedChildren="Yes" unCheckedChildren="No"/>
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="is_critical" label="Critical" valuePropName="checked">
                            <Switch checkedChildren="Yes" unCheckedChildren="No"/>
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="status" label="Active" valuePropName="checked">
                            <Switch checkedChildren="Yes" unCheckedChildren="No"/>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Drawer>
    );
}

// ── Indicator Row ─────────────────────────────────────────────────────────
function IndicatorRow({ ind, onEdit, onDelete }) {
    const t = getIndicatorType(ind.indicator_type);
    return (
        <div style={{
            display:'flex', alignItems:'center', gap:10,
            padding:'8px 12px', marginBottom:4,
            background:'#fafbff', borderRadius:6,
            border:'1px solid #e8f0fe',
            borderLeft:`3px solid ${t.color}`,
        }}>
            <div style={{ flexShrink:0 }}>
                <Tag color={t.color} style={{ fontSize:10, margin:0 }}>{t.label}</Tag>
            </div>
            {ind.code && (
                <span style={{ fontSize:11, fontWeight:700, color:'#888', flexShrink:0 }}>{ind.code}</span>
            )}
            <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#1f3864',
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {ind.title}
                </div>
                {ind.description && (
                    <div style={{ fontSize:11, color:'#888', marginTop:1,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {ind.description}
                    </div>
                )}
            </div>
            <div style={{ display:'flex', gap:6, alignItems:'center', flexShrink:0 }}>
                {ind.target_value && (
                    <Tag style={{ fontSize:10 }}>Target: {ind.target_value}</Tag>
                )}
                {ind.measurement_unit && (
                    <Tag color="default" style={{ fontSize:10 }}>
                        {MEASUREMENT_UNITS.find(u => u.value === ind.measurement_unit)?.label || ind.measurement_unit}
                    </Tag>
                )}
                {ind.is_mandatory == 1 && <Tag color="red" style={{ fontSize:10 }}>Required</Tag>}
                {ind.is_critical  == 1 && <Tag color="volcano" style={{ fontSize:10 }}>Critical</Tag>}
                <Tooltip title="Edit">
                    <Button size="small" icon={<EditOutlined/>} onClick={() => onEdit(ind)}/>
                </Tooltip>
                <Popconfirm title="Delete this indicator?" onConfirm={() => onDelete(ind.id)} okButtonProps={{ danger:true }}>
                    <Button size="small" icon={<DeleteOutlined/>} danger/>
                </Popconfirm>
            </div>
        </div>
    );
}

// ── Section Tree Node ─────────────────────────────────────────────────────
function SectionNode({ section, allSections, indicators, depth=0,
    onEditSection, onDeleteSection, onAddChild, onAddIndicator, onEditIndicator, onDeleteIndicator }) {

    const [expanded, setExpanded] = useState(true);
    const children   = allSections.filter(s => String(s.parent_id) === String(section.id));
    const sectionInd = indicators.filter(i => String(i.section_id) === String(section.id));
    const hasContent = children.length > 0 || sectionInd.length > 0;

    const borderColors = ['#1f3864','#1890ff','#7c3aed','#52c41a','#fa8c16'];
    const borderColor  = borderColors[depth % borderColors.length];

    return (
        <div style={{ marginBottom:6 }}>
            <div style={{
                display:'flex', alignItems:'center', gap:8,
                padding:'8px 12px',
                background: depth === 0 ? '#1f3864' : depth === 1 ? '#f0f5ff' : '#fafbff',
                borderRadius:8,
                border:`1px solid ${depth === 0 ? '#2d5a8e' : '#e0eaff'}`,
                borderLeft:`4px solid ${borderColor}`,
                marginLeft: depth * 20,
            }}>
                {/* Expand toggle */}
                <button onClick={() => setExpanded(e => !e)}
                    style={{ background:'none', border:'none', cursor:'pointer', padding:0,
                        color: depth === 0 ? '#fff' : '#555', fontSize:12, flexShrink:0 }}>
                    {hasContent ? (expanded ? <DownOutlined/> : <RightOutlined/>) : <span style={{ width:12, display:'inline-block' }}/>}
                </button>

                {/* Code badge */}
                {section.code && (
                    <span style={{
                        background: depth === 0 ? 'rgba(255,255,255,0.2)' : borderColor,
                        color: depth === 0 ? '#fff' : '#fff',
                        padding:'1px 8px', borderRadius:4, fontSize:11, fontWeight:800, flexShrink:0
                    }}>{section.code}</span>
                )}

                {/* Title */}
                <span style={{
                    flex:1, fontWeight: depth === 0 ? 800 : 600, fontSize: depth === 0 ? 14 : 13,
                    color: depth === 0 ? '#fff' : '#1f3864',
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'
                }}>{section.title}</span>

                {/* Badges */}
                <div style={{ display:'flex', gap:4, alignItems:'center', flexShrink:0 }}>
                    {sectionInd.length > 0 && (
                        <Tag color={depth===0?'geekblue':'blue'} style={{ fontSize:10, margin:0 }}>
                            {sectionInd.length} indicator{sectionInd.length!==1?'s':''}
                        </Tag>
                    )}
                    {children.length > 0 && (
                        <Tag color={depth===0?'purple':'default'} style={{ fontSize:10, margin:0 }}>
                            {children.length} sub-section{children.length!==1?'s':''}
                        </Tag>
                    )}
                </div>

                {/* Actions */}
                <Space size={4} style={{ flexShrink:0 }}>
                    <Tooltip title="Add Sub-section">
                        <Button size="small" icon={<ApartmentOutlined/>}
                            style={{ fontSize:11,
                                background: depth===0?'rgba(255,255,255,0.15)':'#f0f5ff',
                                borderColor: depth===0?'rgba(255,255,255,0.3)':'#d6e4ff',
                                color: depth===0?'#fff':'#1890ff' }}
                            onClick={() => onAddChild(section)}>
                            Sub-section
                        </Button>
                    </Tooltip>
                    <Tooltip title="Add Indicator">
                        <Button size="small" icon={<BarsOutlined/>} type="primary"
                            style={{ fontSize:11, background:'#7c3aed', borderColor:'#7c3aed' }}
                            onClick={() => onAddIndicator(section)}>
                            Indicator
                        </Button>
                    </Tooltip>
                    <Tooltip title="Edit Section">
                        <Button size="small" icon={<EditOutlined/>}
                            style={{ background: depth===0?'rgba(255,255,255,0.15)':'', color: depth===0?'#fff':'' }}
                            onClick={() => onEditSection(section)}/>
                    </Tooltip>
                    <Popconfirm title="Delete this section and all its contents?"
                        onConfirm={() => onDeleteSection(section.id)} okButtonProps={{ danger:true }}>
                        <Button size="small" icon={<DeleteOutlined/>} danger/>
                    </Popconfirm>
                </Space>
            </div>

            {/* Expanded content */}
            {expanded && (
                <div style={{ marginLeft: (depth + 1) * 20, marginTop:4 }}>
                    {/* Indicators in this section */}
                    {sectionInd.map(ind => (
                        <IndicatorRow
                            key={ind.id} ind={ind}
                            onEdit={onEditIndicator}
                            onDelete={onDeleteIndicator}
                        />
                    ))}

                    {/* Child sections */}
                    {children
                        .sort((a,b) => (a.display_order||0) - (b.display_order||0))
                        .map(child => (
                            <SectionNode
                                key={child.id}
                                section={child}
                                allSections={allSections}
                                indicators={indicators}
                                depth={depth + 1}
                                onEditSection={onEditSection}
                                onDeleteSection={onDeleteSection}
                                onAddChild={onAddChild}
                                onAddIndicator={onAddIndicator}
                                onEditIndicator={onEditIndicator}
                                onDeleteIndicator={onDeleteIndicator}
                            />
                        ))
                    }
                </div>
            )}
        </div>
    );
}

// ── Bulk Import Modal ─────────────────────────────────────────────────────
// Paste tab-separated or numbered list of sections/indicators
function BulkImportDrawer({ visible, onClose, frameworkId, onImported }) {
    const [text,    setText]    = useState('');
    const [saving,  setSaving]  = useState(false);
    const [preview, setPreview] = useState([]);

    const parseText = (raw) => {
        const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);
        const parsed = [];
        lines.forEach(line => {
            // Detect indent level by leading spaces or tabs
            const indent = (line.match(/^(\s+)/) || ['',''])[1].length;
            const clean  = line.replace(/^[\s\d\.\-]+/, '').trim();
            if (!clean) return;
            // Detect if it looks like a section (short) or indicator (longer)
            const isSection = clean.length < 80 && !clean.includes(':');
            parsed.push({ indent, text: clean, isSection, depth: Math.floor(indent / 2) });
        });
        return parsed;
    };

    useEffect(() => {
        if (text) setPreview(parseText(text));
        else      setPreview([]);
    }, [text]);

    const handleImport = async () => {
        if (!preview.length) return;
        setSaving(true);
        try {
            // Build section records from parsed lines
            // Group top-level as sections, indented as sub-sections or indicators
            const sections = preview
                .filter(p => p.isSection)
                .map((p, i) => ({
                    framework_id:  frameworkId,
                    code:          String(i + 1),
                    title:         p.text,
                    parent_id:     null,
                    weight:        1,
                    display_order: i,
                }));

            if (sections.length > 0) {
                const r = await MeService.bulkSaveSections(sections);
                if (r.data?.code !== 200) throw new Error(r.data?.message);
                message.success(`${sections.length} sections imported`);
                onImported();
                onClose();
                setText('');
            }
        } catch(e) { message.error(e.message); }
        finally { setSaving(false); }
    };

    return (
        <Drawer
            title={<span style={{ fontWeight:800 }}>📥 Bulk Import Structure</span>}
            open={visible} onClose={onClose} width={520} destroyOnClose
            footer={
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" loading={saving} disabled={preview.length===0}
                        onClick={handleImport} style={{ background:'#1f3864', borderColor:'#1f3864' }}>
                        Import {preview.filter(p=>p.isSection).length} Sections
                    </Button>
                </div>
            }
        >
            <div style={{ marginBottom:12, fontSize:13, color:'#555', lineHeight:1.6 }}>
                Paste a list of section/clause titles — one per line. Indent with spaces or tabs to create hierarchy.
                Longer lines are treated as indicators.
            </div>
            <Input.TextArea
                rows={12}
                placeholder={`Example:\n1. Access and Continuity of Care\n  1.1 Patient registration\n  1.2 Referral process\n2. Care of the Patient\n  2.1 Clinical assessment\n    Required: Documented assessment for every patient`}
                value={text}
                onChange={e => setText(e.target.value)}
                style={{ fontFamily:'monospace', fontSize:12 }}
            />
            {preview.length > 0 && (
                <div style={{ marginTop:12 }}>
                    <div style={{ fontWeight:700, marginBottom:6, fontSize:12 }}>
                        Preview ({preview.filter(p=>p.isSection).length} sections, {preview.filter(p=>!p.isSection).length} indicators):
                    </div>
                    <div style={{ maxHeight:200, overflowY:'auto', background:'#f8faff',
                        border:'1px solid #e0eaff', borderRadius:6, padding:8 }}>
                        {preview.map((p, i) => (
                            <div key={i} style={{ paddingLeft: p.depth * 16, fontSize:12,
                                color: p.isSection ? '#1f3864' : '#7c3aed',
                                fontWeight: p.isSection ? 700 : 400, marginBottom:2 }}>
                                {p.isSection ? '📁' : '📌'} {p.text}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Drawer>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────
export default function MeFrameworkDetail() {
    const history     = useHistory();
    const { id }      = useParams();
    const frameworkId = parseInt(id);

    const [framework,     setFramework]     = useState(null);
    const [sections,      setSections]      = useState([]);   // flat list
    const [indicators,    setIndicators]    = useState([]);
    const [loading,       setLoading]       = useState(true);

    // Section form
    const [secForm,       setSecForm]       = useState(false);
    const [secEdit,       setSecEdit]       = useState(null);
    const [secParent,     setSecParent]     = useState(null);

    // Indicator form
    const [indForm,       setIndForm]       = useState(false);
    const [indEdit,       setIndEdit]       = useState(null);
    const [indSection,    setIndSection]    = useState(null);

    // Bulk import
    const [bulkImport,    setBulkImport]    = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [fwRes, secRes, indRes] = await Promise.all([
                MeService.getFrameworkDetail(frameworkId),
                MeService.getSections(frameworkId),
                MeService.getIndicators({ framework_id: frameworkId }),
            ]);
            setFramework(fwRes.data?.data || null);
            // Flatten the section tree for easy lookups
            const flattenTree = (nodes, result=[]) => {
                nodes.forEach(n => {
                    result.push(n);
                    if (n.children?.length) flattenTree(n.children, result);
                });
                return result;
            };
            setSections(flattenTree(fwRes.data?.data?.sections || secRes.data?.data || []));
            setIndicators(indRes.data?.data || []);
        } catch(e) { message.error('Failed to load framework'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [frameworkId]); // eslint-disable-line

    // Section actions
    const openAddSection  = ()      => { setSecEdit(null); setSecParent(null); setSecForm(true); };
    const openAddChild    = (sec)   => { setSecEdit(null); setSecParent(sec);  setSecForm(true); };
    const openEditSection = (sec)   => { setSecEdit(sec);  setSecParent(null); setSecForm(true); };

    const handleDeleteSection = async (id) => {
        try {
            const r = await MeService.deleteSection(id);
            if (r.data?.code !== 200) throw new Error(r.data?.message);
            message.success('Section deleted');
            load();
        } catch(e) { message.error(e.message); }
    };

    // Indicator actions
    const openAddIndicator  = (sec)  => { setIndEdit(null); setIndSection(sec); setIndForm(true); };
    const openEditIndicator = (ind)  => { setIndEdit(ind);  setIndSection(null); setIndForm(true); };

    const handleDeleteIndicator = async (id) => {
        try {
            const r = await MeService.deleteIndicator(id);
            if (r.data?.code !== 200) throw new Error(r.data?.message);
            message.success('Indicator deleted');
            load();
        } catch(e) { message.error(e.message); }
    };

    // Top-level sections only
    const rootSections = sections
        .filter(s => !s.parent_id)
        .sort((a,b) => (a.display_order||0) - (b.display_order||0));

    const totalIndicators = indicators.length;
    const mandatoryCount  = indicators.filter(i => i.is_mandatory == 1).length;
    const criticalCount   = indicators.filter(i => i.is_critical  == 1).length;

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto" style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <Button icon={<ArrowLeftOutlined/>} size="small"
                        onClick={() => history.push('/me/frameworks')}>
                        Back
                    </Button>
                    <div>
                        <h4 style={{ margin:0 }}>
                            ⚙️ {framework?.name || 'Framework Structure'}
                        </h4>
                        <small className="text-muted">
                            {framework?.code} · Sections &amp; Indicators
                        </small>
                    </div>
                </div>
                <div className="my-auto ml-auto" style={{ display:'flex', gap:8 }}>
                    <Tooltip title="Download the Excel template to guide your framework structure before importing">
                        <Button icon={<DownloadOutlined/>}
                            href={`${window.location.origin}${window.location.pathname.includes('qoctesting') ? '/qoctesting' : '/qoc'}/api/uploads/templates/ME_Framework_Import_Template.xlsx`}
                            download="ME_Framework_Import_Template.xlsx"
                            target="_blank">
                            Download Template
                        </Button>
                    </Tooltip>
                    <Button icon={<ImportOutlined/>} onClick={() => setBulkImport(true)}>
                        Bulk Import
                    </Button>
                    <Button icon={<ApartmentOutlined/>}
                        onClick={openAddSection}
                        style={{ borderColor:'#1890ff', color:'#1890ff' }}>
                        Add Section
                    </Button>
                    <Button type="primary" icon={<BarsOutlined/>}
                        style={{ background:'#7c3aed', borderColor:'#7c3aed' }}
                        onClick={() => {
                            if (sections.length === 0) {
                                message.warning('Add at least one section first');
                                return;
                            }
                            setIndSection(sections[0]);
                            setIndForm(true);
                        }}>
                        Add Indicator
                    </Button>
                </div>
            </div>

            <div className="page-pad">
                {loading ? (
                    <div style={{ textAlign:'center', padding:60 }}><Spin size="large"/></div>
                ) : (
                    <>
                        {/* Framework summary strip */}
                        {framework && (
                            <Card size="small" style={{ marginBottom:16,
                                borderLeft:`4px solid #1f3864`, background:'#f8faff' }}>
                                <Row gutter={16} align="middle">
                                    <Col flex="auto">
                                        <Space wrap>
                                            <Tag color="blue">{framework.type}</Tag>
                                            {framework.issuing_body && <Tag>{framework.issuing_body}</Tag>}
                                            {framework.version && <Tag>v{framework.version}</Tag>}
                                            <Badge status={framework.status==='active'?'success':'default'} text={framework.status}/>
                                        </Space>
                                        {framework.description && (
                                            <div style={{ fontSize:12, color:'#888', marginTop:4 }}>{framework.description}</div>
                                        )}
                                    </Col>
                                    <Col>
                                        <Row gutter={16}>
                                            {[
                                                { label:'Sections',   val:sections.length,   color:'#1890ff' },
                                                { label:'Indicators', val:totalIndicators,   color:'#7c3aed' },
                                                { label:'Mandatory',  val:mandatoryCount,    color:'#f5222d' },
                                                { label:'Critical',   val:criticalCount,     color:'#fa541c' },
                                            ].map(k => (
                                                <Col key={k.label}>
                                                    <Statistic title={k.label} value={k.val}
                                                        valueStyle={{ color:k.color, fontSize:18, fontWeight:800 }}/>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Col>
                                </Row>
                            </Card>
                        )}

                        {/* Empty state */}
                        {rootSections.length === 0 && (
                            <Card style={{ textAlign:'center', padding:40 }}>
                                <div style={{ fontSize:40, marginBottom:12 }}>🏗️</div>
                                <div style={{ fontSize:16, fontWeight:600, color:'#1f3864', marginBottom:4 }}>
                                    No structure yet
                                </div>
                                <div style={{ color:'#888', marginBottom:16 }}>
                                    Start by adding sections (clauses, pillars, priority areas), then add indicators inside each section.
                                </div>
                                <Space>
                                    <Button icon={<DownloadOutlined/>}
                                        href={`${window.location.origin}${window.location.pathname.includes('qoctesting') ? '/qoctesting' : '/qoc'}/api/uploads/templates/ME_Framework_Import_Template.xlsx`}
                                        download="ME_Framework_Import_Template.xlsx"
                                        target="_blank">
                                        Download Template
                                    </Button>
                                    <Button icon={<ImportOutlined/>} onClick={() => setBulkImport(true)}>
                                        Bulk Import
                                    </Button>
                                    <Button type="primary" icon={<ApartmentOutlined/>}
                                        style={{ background:'#1f3864', borderColor:'#1f3864' }}
                                        onClick={openAddSection}>
                                        Add First Section
                                    </Button>
                                </Space>
                            </Card>
                        )}

                        {/* Section tree */}
                        {rootSections.map(sec => (
                            <SectionNode
                                key={sec.id}
                                section={sec}
                                allSections={sections}
                                indicators={indicators}
                                depth={0}
                                onEditSection={openEditSection}
                                onDeleteSection={handleDeleteSection}
                                onAddChild={openAddChild}
                                onAddIndicator={openAddIndicator}
                                onEditIndicator={openEditIndicator}
                                onDeleteIndicator={handleDeleteIndicator}
                            />
                        ))}
                    </>
                )}
            </div>

            {/* Section form */}
            <SectionForm
                visible={secForm}
                onClose={() => setSecForm(false)}
                onSaved={load}
                frameworkId={frameworkId}
                editRecord={secEdit ? { ...secEdit } : secParent ? { parent_id: secParent.id, framework_id: frameworkId } : null}
                sections={sections}
            />

            {/* Indicator form */}
            <IndicatorForm
                visible={indForm}
                onClose={() => setIndForm(false)}
                onSaved={load}
                frameworkId={frameworkId}
                sectionId={indEdit ? indEdit.section_id : indSection?.id}
                editRecord={indEdit}
            />

            {/* Bulk import */}
            <BulkImportDrawer
                visible={bulkImport}
                onClose={() => setBulkImport(false)}
                frameworkId={frameworkId}
                onImported={load}
            />
        </div>
    );
}
